/**
 * TaskFilters Component
 *
 * Filter panel for the task board/list.
 * Provides filtering by status, priority, labels, and assignee.
 *
 * @module components/tasks/TaskFilters
 */

import { memo, useState, useCallback, useMemo } from 'react';
import {
  Filter,
  X,
  ChevronDown,
  ChevronUp,
  Check,
  Search,
  RotateCcw
} from 'lucide-react';
import TaskStatusBadge, { TASK_STATUS_CONFIG } from './TaskStatusBadge.jsx';
import TaskPriorityBadge, { TASK_PRIORITY_CONFIG } from './TaskPriorityBadge.jsx';
import TaskLabelBadge from './TaskLabelBadge.jsx';
import { Avatar } from '../common/Avatar.jsx';

// ============================================
// Constants
// ============================================

/**
 * All available status options
 */
const STATUS_OPTIONS = Object.entries(TASK_STATUS_CONFIG).map(([value, config]) => ({
  value,
  label: config.label
}));

/**
 * All available priority options
 */
const PRIORITY_OPTIONS = Object.entries(TASK_PRIORITY_CONFIG).map(([value, config]) => ({
  value,
  label: config.label
}));

// ============================================
// Helper Components
// ============================================

/**
 * Collapsible filter section
 */
const FilterSection = memo(function FilterSection({
  title,
  isExpanded,
  onToggle,
  activeCount = 0,
  children
}) {
  return (
    <div className="border-b border-gray-700 last:border-0">
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center justify-between px-4 py-3 hover:bg-gray-800/50 transition-colors"
      >
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-gray-300">{title}</span>
          {activeCount > 0 && (
            <span className="text-xs bg-indigo-600 text-white px-1.5 py-0.5 rounded-full">
              {activeCount}
            </span>
          )}
        </div>
        {isExpanded ? (
          <ChevronUp className="w-4 h-4 text-gray-500" />
        ) : (
          <ChevronDown className="w-4 h-4 text-gray-500" />
        )}
      </button>
      {isExpanded && (
        <div className="px-4 pb-3">
          {children}
        </div>
      )}
    </div>
  );
});

/**
 * Checkbox option for multi-select filters
 */
const CheckboxOption = memo(function CheckboxOption({
  checked,
  onChange,
  children
}) {
  return (
    <button
      type="button"
      onClick={onChange}
      className={`
        flex items-center gap-2 w-full px-2 py-1.5 rounded
        transition-colors text-left
        ${checked
          ? 'bg-indigo-600/20 text-gray-200'
          : 'hover:bg-gray-800 text-gray-400'
        }
      `}
    >
      <div className={`
        w-4 h-4 rounded border flex items-center justify-center
        ${checked
          ? 'bg-indigo-600 border-indigo-600'
          : 'border-gray-600'
        }
      `}>
        {checked && <Check className="w-3 h-3 text-white" />}
      </div>
      {children}
    </button>
  );
});

/**
 * Search input for quick filtering
 */
const SearchInput = memo(function SearchInput({ value, onChange, placeholder }) {
  return (
    <div className="relative">
      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="
          w-full pl-9 pr-3 py-2 bg-gray-800 border border-gray-700 rounded-lg
          text-sm text-gray-200 placeholder-gray-500
          focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent
        "
      />
      {value && (
        <button
          type="button"
          onClick={() => onChange('')}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300"
        >
          <X className="w-4 h-4" />
        </button>
      )}
    </div>
  );
});

// ============================================
// Main Component
// ============================================

/**
 * TaskFilters Component
 *
 * @param {object} props - Component props
 * @param {object} props.filters - Current filter values
 * @param {string} [props.filters.searchQuery=''] - Text search query
 * @param {string[]} [props.filters.status=[]] - Selected status values
 * @param {string[]} [props.filters.priority=[]] - Selected priority values
 * @param {string[]} [props.filters.labels=[]] - Selected label IDs
 * @param {string} [props.filters.assigneeId] - Selected assignee ID
 * @param {function} props.onFiltersChange - Filter change handler
 * @param {array} [props.availableLabels=[]] - Available labels for filtering
 * @param {array} [props.availableAssignees=[]] - Available assignees for filtering
 * @param {boolean} [props.isOpen=false] - Whether filter panel is open
 * @param {function} [props.onClose] - Close handler
 * @param {string} [props.className=''] - Additional CSS classes
 * @returns {React.ReactElement} Filter panel element
 */
const TaskFilters = memo(function TaskFilters({
  filters = {},
  onFiltersChange,
  availableLabels = [],
  availableAssignees = [],
  isOpen = false,
  onClose,
  className = ''
}) {
  // Expanded sections state
  const [expandedSections, setExpandedSections] = useState({
    status: true,
    priority: true,
    labels: false,
    assignee: false
  });

  // Toggle section expansion
  const toggleSection = useCallback((section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  }, []);

  // Update a single filter
  const updateFilter = useCallback((key, value) => {
    onFiltersChange({
      ...filters,
      [key]: value
    });
  }, [filters, onFiltersChange]);

  // Toggle array filter value
  const toggleArrayFilter = useCallback((key, value) => {
    const current = filters[key] || [];
    const newValues = current.includes(value)
      ? current.filter(v => v !== value)
      : [...current, value];
    updateFilter(key, newValues);
  }, [filters, updateFilter]);

  // Reset all filters
  const resetFilters = useCallback(() => {
    onFiltersChange({
      searchQuery: '',
      status: [],
      priority: [],
      labels: [],
      assigneeId: null
    });
  }, [onFiltersChange]);

  // Count active filters
  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (filters.searchQuery) count++;
    if (filters.status?.length) count += filters.status.length;
    if (filters.priority?.length) count += filters.priority.length;
    if (filters.labels?.length) count += filters.labels.length;
    if (filters.assigneeId) count++;
    return count;
  }, [filters]);

  // Count per section
  const statusCount = filters.status?.length || 0;
  const priorityCount = filters.priority?.length || 0;
  const labelCount = filters.labels?.length || 0;
  const assigneeCount = filters.assigneeId ? 1 : 0;

  if (!isOpen) return null;

  return (
    <div className={`bg-gray-900 border border-gray-700 rounded-lg shadow-xl ${className}`}>
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-700">
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-gray-400" />
          <span className="text-sm font-medium text-gray-200">Filters</span>
          {activeFilterCount > 0 && (
            <span className="text-xs bg-indigo-600 text-white px-1.5 py-0.5 rounded-full">
              {activeFilterCount}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {activeFilterCount > 0 && (
            <button
              type="button"
              onClick={resetFilters}
              className="flex items-center gap-1 text-xs text-gray-400 hover:text-gray-200 transition-colors"
            >
              <RotateCcw className="w-3 h-3" />
              Reset
            </button>
          )}
          {onClose && (
            <button
              type="button"
              onClick={onClose}
              className="p-1 text-gray-400 hover:text-gray-200 hover:bg-gray-800 rounded transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      {/* Search */}
      <div className="px-4 py-3 border-b border-gray-700">
        <SearchInput
          value={filters.searchQuery || ''}
          onChange={(value) => updateFilter('searchQuery', value)}
          placeholder="Search tasks..."
        />
      </div>

      {/* Filter Sections */}
      <div className="max-h-[400px] overflow-y-auto">
        {/* Status Filter */}
        <FilterSection
          title="Status"
          isExpanded={expandedSections.status}
          onToggle={() => toggleSection('status')}
          activeCount={statusCount}
        >
          <div className="space-y-1">
            {STATUS_OPTIONS.map(opt => (
              <CheckboxOption
                key={opt.value}
                checked={(filters.status || []).includes(opt.value)}
                onChange={() => toggleArrayFilter('status', opt.value)}
              >
                <TaskStatusBadge status={opt.value} size="sm" />
              </CheckboxOption>
            ))}
          </div>
        </FilterSection>

        {/* Priority Filter */}
        <FilterSection
          title="Priority"
          isExpanded={expandedSections.priority}
          onToggle={() => toggleSection('priority')}
          activeCount={priorityCount}
        >
          <div className="space-y-1">
            {PRIORITY_OPTIONS.map(opt => (
              <CheckboxOption
                key={opt.value}
                checked={(filters.priority || []).includes(opt.value)}
                onChange={() => toggleArrayFilter('priority', opt.value)}
              >
                <TaskPriorityBadge priority={opt.value} size="sm" />
              </CheckboxOption>
            ))}
          </div>
        </FilterSection>

        {/* Labels Filter */}
        {availableLabels.length > 0 && (
          <FilterSection
            title="Labels"
            isExpanded={expandedSections.labels}
            onToggle={() => toggleSection('labels')}
            activeCount={labelCount}
          >
            <div className="space-y-1">
              {availableLabels.map(label => (
                <CheckboxOption
                  key={label.id}
                  checked={(filters.labels || []).includes(label.id)}
                  onChange={() => toggleArrayFilter('labels', label.id)}
                >
                  <TaskLabelBadge label={label} size="sm" />
                </CheckboxOption>
              ))}
            </div>
          </FilterSection>
        )}

        {/* Assignee Filter */}
        {availableAssignees.length > 0 && (
          <FilterSection
            title="Assignee"
            isExpanded={expandedSections.assignee}
            onToggle={() => toggleSection('assignee')}
            activeCount={assigneeCount}
          >
            <div className="space-y-1">
              <CheckboxOption
                checked={!filters.assigneeId}
                onChange={() => updateFilter('assigneeId', null)}
              >
                <span className="text-sm">All assignees</span>
              </CheckboxOption>
              <CheckboxOption
                checked={filters.assigneeId === 'unassigned'}
                onChange={() => updateFilter('assigneeId', 'unassigned')}
              >
                <span className="text-sm text-gray-500 italic">Unassigned</span>
              </CheckboxOption>
              {availableAssignees.map(assignee => (
                <CheckboxOption
                  key={assignee.id}
                  checked={filters.assigneeId === assignee.id}
                  onChange={() => updateFilter('assigneeId', assignee.id)}
                >
                  <Avatar
                    name={assignee.displayName || assignee.name}
                    avatarUrl={assignee.avatarUrl}
                    type={assignee.type}
                    size="xs"
                  />
                  <span className="text-sm">{assignee.displayName || assignee.name}</span>
                </CheckboxOption>
              ))}
            </div>
          </FilterSection>
        )}
      </div>
    </div>
  );
});

export default TaskFilters;
